// --- Interfaces ---

export interface ErrorModel {
    type?: string;
    message: string;
    stack?: string;
    error: any;
    messageCustom?: string;
}

interface DFSLoggerConfig {
    enabled: boolean;
    endpointFunc?: (params: { type: string; message: string }) => Promise<any>;
}

interface Config {
    log?: (errorModel: ErrorModel) => void;
    DFSLoggerConfig?: DFSLoggerConfig;
}

// --- Helpers ---

// eslint-disable-next-line no-underscore-dangle
const isCancel = (error: any): boolean => Object.getPrototypeOf(error)?.__CANCEL__ === true;

// --- Class ---

class ErrorsManager {
    private static instance: ErrorsManager;

    private log?: (errorModel: ErrorModel) => void;

    private DFSLoggerConfig: DFSLoggerConfig = { enabled: false };

    constructor() {
        if (ErrorsManager.instance) {
            return ErrorsManager.instance;
        }

        ErrorsManager.instance = this;
    }

    /**
     * Updates the manager configuration for logging and remote tracking.
     */
    public configure(config: Config): void {
        const {
            log,
            DFSLoggerConfig = { enabled: false },
        } = config;

        this.log = log;
        this.DFSLoggerConfig = DFSLoggerConfig;
    }

    /**
     * Sends the error model to the DFS (Data Factory Service) if enabled.
     */
    private async logToDFS(errorModel: ErrorModel): Promise<void> {
        const { enabled, endpointFunc } = this.DFSLoggerConfig;

        if (!enabled || typeof endpointFunc !== 'function') {
            return;
        }

        const { message, stack } = errorModel;

        try {
            const messageObj = { message, stack };

            await endpointFunc({
                type: 'Error',
                message: JSON.stringify(messageObj),
            });
        } catch (errLogToDFS) {
            const styles = {
                title: 'color: brown; font-size: 16px; font-weight: bold;',
                stack: 'color: brown',
            };

            console.error(
                `%c⚠️ ErrorsManager: %c error with logToDFS - ${errLogToDFS}`,
                styles.title,
                styles.stack,
            );
        }
    }

    /**
     * Processes raw errors into a structured ErrorModel, logs them, and returns the model.
     */
    public createError({
        type,
        error,
        messageCustom,
    }: {
        type: string;
        error: any;
        messageCustom?: string
    }): ErrorModel | undefined {
        // Handle cases where 'error' might not be a standard Error object
        const message = error?.message || String(error);
        const stack = error?.stack;

        const errorModel: ErrorModel = {
            type,
            message: `${type ? `${type}: ` : ''}${messageCustom || message}`,
            stack,
            error,
            messageCustom,
        };

        if (isCancel(error)) {
            return undefined;
        }

        if (typeof this.log === 'function') {
            this.log(errorModel);
        }

        this.logToDFS(errorModel);

        return errorModel;
    }
}

const ErrorsManagerInstance = new ErrorsManager();

export default ErrorsManagerInstance;
